import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/SectionHeading";
import { Marquee } from "@/components/site/Marquee";
import { Timeline } from "@/components/site/Timeline";
import { CtaBand } from "@/components/site/CtaBand";
import { Icon } from "@/components/site/Icon";
import {
  IMAGES, INDUSTRIES, SOLUTIONS, TRUST_CARDS, WHY_US, MARQUEE_WORDS,
} from "@/data/content";

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);

  return (
    <>
      {/* ================= HERO ================= */}
      <section ref={heroRef} className="relative flex min-h-[100svh] items-center overflow-hidden bg-slate-950" data-testid="hero">
        <motion.img
          src={IMAGES.hero}
          alt="Global trade and shipping containers"
          style={{ y: bgY, scale: bgScale }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-slate-950/30" />
        <div className="absolute inset-0 bg-slate-950/30" />

        <div className="container-x relative z-10 pt-28 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3"
          >
            <span className="h-px w-10 bg-[color:var(--brand-red)]" />
            <span className="eyebrow text-slate-200">International Sourcing &amp; Trade</span>
          </motion.div>

          <h1 className="mt-6 max-w-5xl font-[Outfit] text-4xl font-extrabold leading-[1.02] text-white sm:text-6xl lg:text-7xl">
            <MaskedLines
              lines={["Global Sourcing.", "Trusted Supply.", ""]}
              lineClassName=""
              delay={0.15}
            />
            <span className="reveal-line">
              <motion.span
                className="block text-[color:var(--brand-red)]"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.15 + 2 * 0.12, ease: [0.22, 1, 0.36, 1] }}
              >
                Seamless Trade.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-8 max-w-2xl text-lg leading-relaxed text-slate-200 sm:text-xl"
          >
            We connect businesses with reliable sourcing, supply and trade solutions across global
            markets — from product sourcing and quality inspection to logistics, warehousing and delivery.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/solutions"
              data-testid="hero-explore-solutions"
              className="group inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-red)] px-7 py-3.5 text-base font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5"
            >
              Explore Our Solutions
              <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/5 px-7 py-3.5 text-base font-semibold text-white backdrop-blur-sm transition-colors duration-200 hover:bg-white hover:text-slate-950"
            >
              Contact Us
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ================= TRUST / INTRO ================= */}
      <section className="relative bg-white py-24 lg:py-32" data-testid="trust-intro">
        <div className="container-x grid gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <SectionHeading
              eyebrow="Who We Are"
              title="Your Trusted Partner in Global Trade"
              subtitle="SPMS Global helps businesses source the right products, from the right suppliers, and move them reliably across markets. We combine sourcing expertise, quality discipline and coordinated logistics to make international trade simpler and more dependable."
            />
            <Reveal delay={0.1} className="mt-8 space-y-4 text-slate-600">
              <p className="leading-relaxed">
                From identifying suppliers and arranging samples to inspection, documentation and
                final delivery, we act as a single, accountable point of contact across your supply chain.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <Link to="/about" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--brand-blue)] hover:underline" data-testid="intro-about-link">
                  More about us <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            {TRUST_CARDS.map((card, i) => (
              <Reveal key={card.title} delay={i * 0.08}>
                <div className="group h-full rounded-2xl border border-slate-200 bg-[#fafafa] p-7 transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg" data-testid={`trust-card-${i}`}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[color:var(--brand-blue)]/10 text-[color:var(--brand-blue)]">
                    <Icon name={card.icon} className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-slate-950">{card.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{card.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= MARQUEE ================= */}
      <Marquee words={MARQUEE_WORDS} />

      {/* ================= INDUSTRIES & PRODUCTS ================= */}
      <section className="bg-[#fafafa] py-24 lg:py-32" data-testid="industries-section">
        <div className="container-x">
          <SectionHeading
            align="center"
            eyebrow="Industries & Products"
            title="Industries & Products We Deal In"
            subtitle="Serving diverse industries through reliable global sourcing and trade partnerships."
          />

          <div className="mx-auto mt-14 grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.slug} delay={(i % 3) * 0.07}>
                <Link
                  to="/industries"
                  className="group flex h-full items-center gap-4 rounded-2xl border border-slate-200 bg-white p-6 transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg"
                  data-testid={`industry-card-${ind.slug}`}
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[color:var(--brand-blue)]/10 text-[color:var(--brand-blue)] transition-colors duration-300 group-hover:bg-[color:var(--brand-blue)] group-hover:text-white">
                    <Icon name={ind.icon} className="h-6 w-6" />
                  </div>
                  <h3 className="text-base font-bold leading-snug text-slate-950">{ind.name}</h3>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= SOLUTIONS PREVIEW ================= */}
      <section className="relative overflow-hidden bg-slate-950 py-24 lg:py-32" data-testid="solutions-preview">
        <div className="pointer-events-none absolute -right-40 top-0 h-96 w-96 rounded-full bg-[color:var(--brand-blue)] opacity-20 blur-3xl" />
        <div className="container-x relative z-10">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <SectionHeading
              light
              eyebrow="Our Solutions"
              title="More than trade. An end-to-end business solution."
              subtitle="Rely on us across every stage of the supply chain — sourcing, logistics, inspection, warehousing and complete trade support."
            />
            <Reveal delay={0.15}>
              <Link to="/solutions" className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold text-white hover:underline" data-testid="solutions-viewall">
                View all solutions <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SOLUTIONS.slice(0, 6).map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.08}>
                <div className="group h-full rounded-2xl border border-white/10 bg-white/[0.03] p-7 transition-colors duration-300 hover:border-white/25 hover:bg-white/[0.06]" data-testid={`solution-preview-${i}`}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[color:var(--brand-red)]/15 text-[color:var(--brand-red)] transition-transform duration-300 group-hover:scale-110">
                    <Icon name={s.icon} className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= HOW WE WORK ================= */}
      <section className="bg-white py-24 lg:py-32" data-testid="how-we-work">
        <div className="container-x">
          <SectionHeading
            eyebrow="How We Work"
            title="A clear, six-step process"
            subtitle="From understanding your requirement to final delivery — transparent at every stage."
          />
          <div className="mt-16">
            <Timeline />
          </div>
        </div>
      </section>

      {/* ================= WHY US ================= */}
      <section className="bg-[#fafafa] py-24 lg:py-32" data-testid="why-us">
        <div className="container-x">
          <SectionHeading
            align="center"
            eyebrow="Why Choose Us"
            title="Why Businesses Choose Us"
            subtitle="A partner built around reliability, expertise and long-term relationships."
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_US.map((w, i) => (
              <Reveal key={w.title} delay={(i % 3) * 0.08}>
                <div className="flex h-full gap-4 rounded-2xl border border-slate-200 bg-white p-7 transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg" data-testid={`why-card-${i}`}>
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[color:var(--brand-blue)]/10 text-[color:var(--brand-blue)]">
                    <Icon name={w.icon} className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-950">{w.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{w.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
};

export default Home;
